// Go-live tracker import client (plan §4 POST /imports/tracker?dryRun=, spec §5.10; owner only,
// import.tracker). The upload is multipart, which the JSON helper in http.ts does not send, so it
// has a small sender here with the same rules as the pack upload: cookie mode, a checked path,
// typed errors and a parsed answer.
import { ApiError, NetworkError, UNEXPECTED_RESPONSE } from './errors.ts';
import { idempotencyKey as checkedKey } from './headers.ts';
import { API_BASE_PATH, apiErrorFrom, type FetchLike, type HttpOptions } from './http.ts';
import { assertSafePath } from './paths.ts';
import { trackerReportSchema, type TrackerReport } from './schemas/imports.ts';

export * from './schemas/imports.ts';

export interface TrackerImportOptions {
  /** True reads and reports only; false writes in one transaction. */
  readonly dryRun: boolean;
  /** The typed force phrase, sent only when the portal already holds state. */
  readonly force?: string;
  readonly idempotencyKey?: string;
}

export interface ImportsApi {
  /** POST /imports/tracker (multipart field "file", optional "force"). */
  tracker(file: Blob, opts: TrackerImportOptions): Promise<TrackerReport>;
}

const FILE_NAME = 'tracker.xlsx';
const UNEXPECTED_MESSAGE = 'The portal sent an answer this page did not expect. Reload the page.';

const isAbort = (err: unknown): boolean => err instanceof Error && err.name === 'AbortError';

function formOf(file: Blob, force: string | undefined): FormData {
  const form = new FormData();
  form.append('file', file, file instanceof File ? file.name : FILE_NAME);
  if (force !== undefined) form.append('force', force);
  return form;
}

export function createImportsApi(options: HttpOptions = {}): ImportsApi {
  const base = options.baseUrl ?? API_BASE_PATH;
  const doFetch: FetchLike = options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  return {
    async tracker(file, opts) {
      // No Content-Type: the browser writes the multipart boundary itself.
      const headers = new Headers({ Accept: 'application/json' });
      if (opts.idempotencyKey !== undefined) {
        headers.set('Idempotency-Key', checkedKey(opts.idempotencyKey));
      }
      const path = assertSafePath(`/imports/tracker?dryRun=${String(opts.dryRun)}`);
      let res: Response;
      try {
        res = await doFetch(`${base}${path}`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: formOf(file, opts.force),
        });
      } catch (err) {
        if (isAbort(err)) throw err;
        throw new NetworkError(err);
      }
      if (!res.ok) throw await apiErrorFrom(res);
      const parsed = trackerReportSchema.safeParse(await res.json().catch(() => undefined));
      if (!parsed.success) throw new ApiError(res.status, UNEXPECTED_RESPONSE, UNEXPECTED_MESSAGE);
      return parsed.data;
    },
  };
}
